import { useCallback, useEffect, useRef, useState } from 'react';

export type UseIntervalOptions = {
	autoInvoke?: boolean;
};

export type UseIntervalReturnValue = {
	start: () => void;
	stop: () => void;
	toggle: () => void;
	active: boolean;
};

export function useInterval(
	fn: () => void,
	interval: number,
	{ autoInvoke = false }: UseIntervalOptions = {}
): UseIntervalReturnValue {
	const [active, setActive] = useState(false);
	const intervalRef = useRef<number | null>(null);
	const fnRef = useRef<() => void>(null);

	const start = useCallback(() => {
		setActive((old) => {
			if (!old && (!intervalRef.current || intervalRef.current === -1)) {
				intervalRef.current = window.setInterval(fnRef.current!, interval);
			}
			return true;
		});
	}, []);

	const stop = useCallback(() => {
		setActive(false);
		window.clearInterval(intervalRef.current || -1);
		intervalRef.current = -1;
	}, []);

	const toggle = useCallback(() => {
		if (active) {
			stop();
			return;
		}

		start();
	}, [active]);

	useEffect(() => {
		// @ts-ignore
		fnRef.current = fn;
		active && start();
		return stop;
	}, [fn, active, interval]);

	useEffect(() => {
		if (autoInvoke) start();
	}, []);

	return { start, stop, toggle, active };
}
